import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./ReactionLogin.css";

const ReactionLogin = () => {
  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [service, setService] = useState("");

  // ==========================================
  // ACCESS CHECK
  // ==========================================

  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      const authed = localStorage.getItem("rg_auth") === "1";
      const email = localStorage.getItem("user_email");

      if (!authed || !email) {
        navigate("/dashboard-login");
        return;
      }

      try {
        const res = await axios.get("/check-access", {
          params: {
            email,
            test: "reaction_speed",
          },
        });

        console.log("Reaction Speed API:", res.data);

        if (!res.data.allowed) {
          alert("Access denied");
          navigate("/");
          return;
        }

        // Get service from API response or localStorage
        const userService =
          res.data.service ||
          res.data.metadata?.service ||
          res.data.user?.metadata?.service ||
          localStorage.getItem("service") ||
          "";

        if (isMounted) {
          setService(userService);
        }
      } catch (err) {
        console.error("Reaction access check failed:", err);

        alert("Unable to verify access.");
        navigate("/");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // ==========================================
  // SERVICE IDENTIFICATION
  // ==========================================

  const normalizedService = service.trim().toLowerCase();

  const isVisionIgnite =
    normalizedService === "forge your peak - vision ignite";

  const isIgniteOrAscend =
    normalizedService === "forge your peak - ignite" ||
    normalizedService === "forge your peak - ascend";

  // ==========================================
  // RETURN HOME
  // ==========================================

  const returnHome = () => {
    if (isVisionIgnite) {
      navigate("/measure");
    } else if (isIgniteOrAscend) {
      navigate("/ignite/dashboard");
    } else {
      navigate("/");
    }
  };

  // ==========================================
  // START GAME
  // ==========================================

  const startGame = () => {
    navigate("/reactiongame/play");
  };

  // ==========================================
  // LOADING SCREEN
  // ==========================================

  if (loading) {
    return (
      <div className="rg-login-wrapper">
        <p style={{ textAlign: "center" }}>
          Checking access...
        </p>
      </div>
    );
  }

  // ==========================================
  // SERVICE-BASED CONTENT
  // ==========================================

  const pageTitle = isVisionIgnite
    ? "Reaction Speed Assessment"
    : "Reaction Speed (Simple)";

  const pageDescription = isVisionIgnite
    ? "Measure how quickly you respond to a visual signal. Reaction speed involves visual processing, attention, and motor response."
    : "Simple reaction speed measures how fast you can respond to a sudden signal — the moment your brain says GO. It is a core layer beneath agility, shot timing, saves, and explosive first steps.";

  return (
    <div className="rg-login-wrapper">

      {/* ==========================================
          RETURN HOME BUTTON
      ========================================== */}

      <div className="return-home-wrapper">
        <button
          type="button"
          onClick={returnHome}
          className="return-home-btn"
        >
          ← Return Home
        </button>
      </div>

      {/* ==========================================
          REACTION HEADER
      ========================================== */}

      <div className="reaction-header-card">

        <h2 className="reaction-title">
          {pageTitle}
        </h2>

        <p className="reaction-subtitle">
          {pageDescription}
        </p>

        <div className="reaction-pill">
          Test type:{" "}
          <strong>
            Visual stimulus → Single tap response
          </strong>
        </div>

        <hr className="reaction-divider" />

        {/* ==========================================
            WHY SECTION
        ========================================== */}

        <div className="reaction-why-card">

          <h3 className="why-title">
            {isVisionIgnite
              ? "Why Reaction Speed Matters"
              : "Why reaction speed matters in sport"}
          </h3>

          {isVisionIgnite ? (
            <>
              <p className="why-description">
                Reaction speed reflects how quickly you respond
                after detecting a visual signal. It involves
                visual processing, attention, decision-making,
                and motor response.
              </p>

              <div className="why-tags">
                <span>Visual Processing</span>
                <span>Attention</span>
                <span>Response Speed</span>
                <span>Focus</span>
                <span>Performance</span>
              </div>

              <ul className="why-list">
                <li>
                  <strong>Visual processing:</strong>{" "}
                  Respond to visual signals as quickly as possible.
                </li>

                <li>
                  <strong>Attention:</strong>{" "}
                  Stay focused on the screen and detect the target.
                </li>

                <li>
                  <strong>Response consistency:</strong>{" "}
                  Observe your reaction times across repeated trials.
                </li>

                <li>
                  <strong>Performance awareness:</strong>{" "}
                  Use your results to understand your current
                  reaction-speed performance.
                </li>
              </ul>
            </>
          ) : (
            <>
              {/* ORIGINAL IGNITE + ASCEND CONTENT */}

              <p className="why-description">
                Most game moments are decided in the first few
                hundred milliseconds. Simple reaction speed is
                your baseline ignition time — how quickly your
                nervous system converts a visual cue into action.
              </p>

              <div className="why-tags">
                <span>Faster First Step</span>
                <span>Quicker Trigger</span>
                <span>Better Saves</span>
                <span>Earlier Swing / Shot</span>
                <span>Less Hesitation</span>
              </div>

              <ul className="why-list">
                <li>
                  <strong>Time is position.</strong>{" "}
                  If you react earlier, you arrive earlier —
                  at the ball, the gap, the tackle, or the catch.
                </li>

                <li>
                  <strong>Reduces late-movement errors.</strong>{" "}
                  Late starts can force rushed movements and
                  poor contact quality.
                </li>

                <li>
                  <strong>Improves confidence under pace.</strong>{" "}
                  A quick response helps you commit sooner
                  and second-guess less.
                </li>

                <li>
                  <strong>Shows fatigue and readiness.</strong>{" "}
                  Reaction speed can vary with sleep, workload,
                  and mental fatigue.
                </li>
              </ul>

              <hr className="why-divider" />

              <h4 className="sport-title">
                Sport-specific examples
              </h4>

              <ul className="sport-list">
                <li>
                  <strong>Cricket / Baseball:</strong>{" "}
                  React to ball release and improve your timing.
                </li>

                <li>
                  <strong>Football / Soccer:</strong>{" "}
                  Respond to a cue to press or intercept.
                </li>

                <li>
                  <strong>Goalkeepers:</strong>{" "}
                  Initiate movement in response to a shot.
                </li>

                <li>
                  <strong>Tennis / Badminton:</strong>{" "}
                  Respond quickly to the opponent's shot.
                </li>
              </ul>
            </>
          )}

        </div>
      </div>

      {/* ==========================================
          HOW SECTION
      ========================================== */}

      <div className="how-card">

        <h2 className="how-title">
          How the Game Works
        </h2>

        <ul className="how-list">

          <li>
            When the screen changes or the target appears,{" "}
            <strong>tap or click immediately.</strong>
          </li>

          <li>
            You will get 5 trials. Reaction time is recorded
            for each trial.
          </li>

          <li>
            False starts — clicking before the green signal —
            do not count.
          </li>

          <li>
            Your reaction speed can vary with fatigue,
            sleep, and stress.
          </li>

        </ul>

        <p className="how-note">
          Best practice: use the same device and posture
          when comparing results.
        </p>

      </div>

      {/* ==========================================
          GAME SECTION
      ========================================== */}

      <div
        className="game-containerr"
        style={{ textAlign: "center" }}
      >

        <h2>
          {isVisionIgnite
            ? "Ready to Assess Your Reaction Speed?"
            : "Get Ready to Test Your Reaction"}
        </h2>

        <p>
          Click Start, wait for green, then click as fast
          as you can.
        </p>

        <button
          type="button"
          onClick={startGame}
          className="start-buttonr"
        >
          Start Game
        </button>

        {error && (
          <p
            style={{
              color: "#e10600",
              marginTop: "14px",
              fontWeight: "600",
            }}
          >
            {error}
          </p>
        )}

      </div>

    </div>
  );
};

export default ReactionLogin;